import React, { useEffect, useState, useContext, useRef } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";

import {
  Dialog,
  DialogContent,
  Paper,
  Typography,
  CircularProgress,
  Button,
  TextField,
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { SocketContext } from "../../context/Socket/SocketContext";

// TTL local do QR: se o mesmo QR ficar exibido por mais que isso sem renovar nem
// conectar, marcamos como expirado no front (evita exibir QR obsoleto e exige
// "Gerar Novo QR-Code" manualmente, sem ficar regerando em loop).
const QR_LOCAL_TTL_MS = 60000;

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const [qrCode, setQrCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // ✅ ANTI-BAN: estado dedicado para QR expirado. Quando true, exibimos uma
  // mensagem no lugar do QR e exigimos clique em "Gerar Novo QR-Code".
  // Não regeramos automaticamente — isso era o que disparava banimento.
  const [expired, setExpired] = useState(false);
  // ✅ ANTI-BAN: cooldown do rate limiter de QR. O backend envia
  // qrcode = "COOLDOWN:<ISO>" quando houve QRs demais na última hora; aqui
  // mostramos o aviso com o horário da retomada automática em vez do spinner.
  const [cooldownUntil, setCooldownUntil] = useState(null);

  // ✅ LOGIN POR NÚMERO (pairing code): método selecionado ("qr" | "phone"),
  // número digitado e o código de pareamento recebido do backend.
  const [method, setMethod] = useState("qr");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  // Número realmente usado no pareamento (vem do backend junto com o código).
  const [pairingNumber, setPairingNumber] = useState("");
  // Quando true, exibe o campo de número mesmo havendo um código já gerado.
  const [editingNumber, setEditingNumber] = useState(false);

  const socketManager = useContext(SocketContext);

  // Identifica esta aba para o lock de "driver" da conexão (coordena com o link público).
  const clientIdRef = useRef(null);
  if (!clientIdRef.current) {
    clientIdRef.current = `int_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }

  // Função para gerar novo QR Code (limpa sessão anterior)
  const generateNewQrCode = async () => {
    if (!whatsAppId) return;

    try {
      setLoading(true);
      setError(false);
      setExpired(false);
      setCooldownUntil(null);
      setQrCode("");
      setPairingCode("");

      await api.post(`/whatsappsession/${whatsAppId}/generate-qr`, { clientId: clientIdRef.current });

      // O Baileys emite o PRIMEIRO QR via Socket.io.
      // Quando ele expirar, o backend marca qrcode = "EXPIRED" e
      // não gera outro automaticamente — o usuário clica no botão.
      console.log(`[QrcodeModal] Solicitação de novo QR Code enviada para ${whatsAppId}`);

    } catch (err) {
      console.error("Erro ao gerar QR Code:", err);
      toastError(err);
      setLoading(false);
      setError(true);
    }
  };

  // ✅ LOGIN POR NÚMERO: solicita um código de pareamento de 8 dígitos.
  const requestPairingCode = async () => {
    if (!whatsAppId) return;

    const digits = (phoneNumber || "").replace(/\D/g, "");
    if (digits.length < 10) {
      toastError({
        response: {
          data: {
            error:
              i18n.t("qrCode.pairing.invalidNumber"),
          },
        },
      });
      return;
    }

    try {
      setLoading(true);
      setError(false);
      setExpired(false);
      setCooldownUntil(null);
      setQrCode("");
      setPairingCode("");
      setEditingNumber(false);

      await api.post(`/whatsappsession/${whatsAppId}/pairing-code`, {
        number: digits,
        clientId: clientIdRef.current,
      });

      // O código chega via Socket.io no campo qrcode com prefixo "PAIR:".
      console.log(`[QrcodeModal] Solicitação de pairing code enviada para ${whatsAppId}`);
    } catch (err) {
      console.error("Erro ao gerar código de pareamento:", err);
      toastError(err);
      setLoading(false);
      setError(true);
    }
  };

  // Função para reiniciar tentativas
  const handleRetry = () => {
    console.log(`[QrcodeModal] Tentando gerar QR Code novamente para ${whatsAppId}...`);
    setError(false);
    setExpired(false);
    setCooldownUntil(null);
    setQrCode("");
    setPairingCode("");
    if (method === "phone") {
      requestPairingCode();
    } else {
      generateNewQrCode();
    }
  };

  // Alterna entre os métodos de login (QR x número de telefone).
  const handleSelectMethod = (next) => {
    if (next === method) return;
    setMethod(next);
    setError(false);
    setExpired(false);
    setCooldownUntil(null);
    setQrCode("");
    setPairingCode("");
    setEditingNumber(false);
    if (next === "qr") {
      // Volta para o fluxo de QR (gera um novo).
      generateNewQrCode();
    } else {
      // Aguarda o usuário informar o número antes de gerar o código.
      setLoading(false);
    }
  };

  useEffect(() => {
    const initModal = async () => {
      if (!whatsAppId || !open) return;

      console.log(`[QrcodeModal] Modal aberto para ${whatsAppId} - gerando novo QR Code...`);

      // ✅ SEMPRE gera novo QR Code ao abrir o modal (método padrão).
      setLoading(true);
      setError(false);
      setQrCode("");
      setPairingCode("");

      try {
        await generateNewQrCode();
      } catch (err) {
        console.error("Erro ao iniciar geração de QR Code:", err);
        toastError(err);
        setLoading(false);
        setError(true);
      }
    };

    if (open) {
      // Sempre começa pelo método QR ao (re)abrir o modal.
      setMethod("qr");
      setPhoneNumber("");
      initModal();
    } else {
      // Limpa estados ao fechar
      setLoading(true);
      setError(false);
      setExpired(false);
      setCooldownUntil(null);
      setQrCode("");
      setPairingCode("");
      setPairingNumber("");
      setEditingNumber(false);
      setMethod("qr");
      setPhoneNumber("");
    }
  }, [open, whatsAppId]);

  useEffect(() => {
    if (!whatsAppId || !open) return;

    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handleSessionUpdate = (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        // Pareamento bloqueado por número duplicado: o backend derrubou e
        // desvinculou a sessão, então o QR na tela nunca vai conectar. Fecha o
        // popup; o aviso ao usuário é disparado na página de Conexões, para não
        // duplicar o toast quando o modal estiver aberto.
        if (data.blocked?.reason === "duplicate_connected_number") {
          console.log(`[QrcodeModal] Pareamento bloqueado para ${whatsAppId}: ${data.blocked.message}`);
          setQrCode("");
          setPairingCode("");
          setLoading(false);
          setError(false);
          onClose();
          return;
        }

        const incomingQr = data.session.qrcode;
        console.log(`[QrcodeModal] Socket recebeu update:`, {
          id: data.session.id,
          status: data.session.status,
          qrcode: incomingQr ? `${incomingQr.substring(0, 30)}...` : 'VAZIO',
          qrcodeLength: incomingQr?.length || 0
        });

        // ✅ LOGIN POR NÚMERO: backend envia "PAIR:<código>:<número>".
        if (incomingQr && incomingQr.startsWith("PAIR:") && data.session.status === "qrcode") {
          console.log(`[QrcodeModal] Pairing code recebido para ${whatsAppId}`);
          const parts = incomingQr.slice(5).split(":");
          setPairingCode(parts[0] || "");
          setPairingNumber(parts[1] || "");
          setQrCode("");
          setExpired(false);
          setLoading(false);
          setError(false);
          return;
        }

        // ✅ ANTI-BAN: sentinel "COOLDOWN:<ISO>" — QRs demais na última hora;
        // geração pausada com retomada automática no horário indicado.
        if (incomingQr && incomingQr.startsWith("COOLDOWN:") && data.session.status === "qrcode") {
          const until = new Date(incomingQr.slice(9));
          console.log(`[QrcodeModal] Cooldown de QR para ${whatsAppId} até ${incomingQr.slice(9)}`);
          setCooldownUntil(Number.isNaN(until.getTime()) ? null : until);
          setQrCode("");
          setPairingCode("");
          setExpired(false);
          setLoading(false);
          setError(false);
          return;
        }

        // ✅ ANTI-BAN: sentinel "EXPIRED" enviado pelo backend após o 1º QR
        // expirar. NÃO regeramos automaticamente — mostramos mensagem +
        // botão "Gerar Novo QR-Code".
        if (incomingQr === "EXPIRED" && data.session.status === "qrcode") {
          console.log(`[QrcodeModal] QR Code expirado para ${whatsAppId} — aguardando ação manual`);
          setQrCode("");
          setPairingCode("");
          setExpired(true);
          setLoading(false);
          setError(false);
          return;
        }

        // ✅ CORREÇÃO: Se QR foi limpo (string vazia), mostra loading
        if (incomingQr === "" && data.session.status === "qrcode") {
          console.log(`[QrcodeModal] QR Code limpo para ${whatsAppId}, aguardando novo QR...`);
          setQrCode("");
          setExpired(false);
          // Em modo número, aguardando o usuário; não força spinner.
          setLoading((prev) => (method === "phone" && !pairingCode ? false : true));
          setError(false);
          return;
        }

        // O Baileys emitiu novo QR Code (renovação automática). Também cobre o
        // fallback: se o pairing code falhar, o backend emite um QR normal.
        if (incomingQr && incomingQr !== "EXPIRED" && !incomingQr.startsWith("PAIR:") && !incomingQr.startsWith("COOLDOWN:") && incomingQr.length > 0 && data.session.status === "qrcode") {
          console.log(`[QrcodeModal] Novo QR Code recebido via Socket.io para ${whatsAppId}`);
          setQrCode(incomingQr);
          setPairingCode("");
          setExpired(false);
          setCooldownUntil(null);
          setLoading(false);
          setError(false);
        }

        // Conexão bem-sucedida
        if (data.session.status === "CONNECTED") {
          console.log(`[QrcodeModal] WhatsApp ${whatsAppId} conectado com sucesso!`);
          setError(false);
          onClose();
        }

        // Se status for 'DISCONNECTED' com retries >= 5, mostra erro
        // (Baileys tentou 5 vezes e falhou - timeout 408)
        if (data.session.status === "DISCONNECTED" && data.session.retries >= 5) {
          console.log(`[QrcodeModal] Falha após ${data.session.retries} tentativas para ${whatsAppId}`);
          setError(true);
          setLoading(false);
        }
      }
    };

    socket.on(`company-${companyId}-whatsappSession`, handleSessionUpdate);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, handleSessionUpdate);
    };
  }, [whatsAppId, open, onClose, socketManager, method, pairingCode]);

  // Expiração local do QR: reinicia a cada novo QR recebido. Se não renovar nem
  // conectar dentro do TTL, marca como expirado e exige geração manual.
  useEffect(() => {
    if (!open || !qrCode) return undefined;
    const timer = setTimeout(() => {
      setQrCode("");
      setExpired(true);
      setLoading(false);
    }, QR_LOCAL_TTL_MS);
    return () => clearTimeout(timer);
  }, [qrCode, open]);

  const renderInstructions = () => (
    <div style={{ marginRight: "20px", maxWidth: "420px" }}>
      <Typography variant="h2" component="h2" color="textPrimary" gutterBottom style={{ fontFamily: "Montserrat", fontWeight: "bold", fontSize: "20px" }}>
        {i18n.t("qrCode.title")}
      </Typography>

      {/* Seletor de método de login */}
      <div style={{ display: "flex", gap: "8px", margin: "8px 0 16px" }}>
        <Button
          size="small"
          variant={method === "qr" ? "contained" : "outlined"}
          color="primary"
          onClick={() => handleSelectMethod("qr")}
        >
          QR Code
        </Button>
        <Button
          size="small"
          variant={method === "phone" ? "contained" : "outlined"}
          color="primary"
          onClick={() => handleSelectMethod("phone")}
        >
          {i18n.t("qrCode.methodPhone")}
        </Button>
      </div>

      {method === "qr" ? (
        <>
          <Typography variant="body1" color="textPrimary" gutterBottom>
            {i18n.t("qrCode.qrSteps.one")}
          </Typography>
          <Typography variant="body1" color="textPrimary" gutterBottom>
            {i18n.t("qrCode.qrSteps.two")}
          </Typography>
          <Typography variant="body1" color="textPrimary" gutterBottom>
            {i18n.t("qrCode.qrSteps.three")}
          </Typography>
          <Typography variant="body1" color="textPrimary" gutterBottom>
            {i18n.t("qrCode.qrSteps.four")}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: "16px", fontStyle: "italic" }}>
            {i18n.t("qrCode.qrSteps.hint")}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body1" color="textPrimary" gutterBottom>
            {i18n.t("qrCode.phoneSteps.one")}
          </Typography>
          <Typography variant="body1" color="textPrimary" gutterBottom>
            {i18n.t("qrCode.phoneSteps.two")}
          </Typography>
          <Typography variant="body1" color="textPrimary" gutterBottom>
            {i18n.t("qrCode.phoneSteps.three")}
          </Typography>
          <Typography variant="body1" color="textPrimary" gutterBottom>
            {i18n.t("qrCode.phoneSteps.four")}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: "16px", fontStyle: "italic" }}>
            {i18n.t("qrCode.phoneSteps.hint")}
          </Typography>
        </>
      )}
    </div>
  );

  const renderRightPanel = () => {
    if (error) {
      return (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Typography variant="h6" color="error" gutterBottom>
            {i18n.t("qrCode.error.title")}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: "16px" }}>
            {i18n.t("qrCode.error.message")}
          </Typography>
          <Button variant="contained" color="primary" onClick={handleRetry}>
            {i18n.t("qrCode.error.retry")}
          </Button>
        </div>
      );
    }

    if (cooldownUntil) {
      const minLeft = Math.max(1, Math.ceil((cooldownUntil.getTime() - Date.now()) / 60000));
      const horario = cooldownUntil.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
      return (
        <div style={{ textAlign: "center", padding: "20px", maxWidth: "300px" }}>
          <Typography variant="h6" color="textPrimary" gutterBottom>
            {i18n.t("qrCode.cooldown.title")}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: "12px" }}>
            {i18n.t("qrCode.cooldown.message")}{" "}
            <strong>{minLeft} min</strong> {i18n.t("qrCode.cooldown.at", { time: horario })}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: "16px" }}>
            {i18n.t("qrCode.cooldown.hint")}
          </Typography>
          <Button variant="outlined" color="primary" onClick={handleRetry}>
            {i18n.t("qrCode.cooldown.retry")}
          </Button>
        </div>
      );
    }

    if (expired) {
      return (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Typography variant="h6" color="textPrimary" gutterBottom>
            {i18n.t("qrCode.expired.title")}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: "16px" }}>
            {i18n.t("qrCode.expired.message")}
          </Typography>
          <Button variant="contained" color="primary" onClick={handleRetry}>
            {i18n.t("qrCode.expired.retry")}
          </Button>
        </div>
      );
    }

    // Modo número de telefone
    if (method === "phone") {
      if (pairingCode && !editingNumber) {
        return (
          <div style={{ textAlign: "center", padding: "12px" }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {i18n.t("qrCode.pairing.enterCode")}
            </Typography>
            <Typography
              variant="h3"
              color="textPrimary"
              style={{ fontFamily: "monospace", letterSpacing: "6px", fontWeight: "bold", margin: "8px 0 4px" }}
            >
              {pairingCode}
            </Typography>
            {pairingNumber && (
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: "12px" }}>
                {i18n.t("qrCode.pairing.number")} <strong>+{pairingNumber}</strong>
              </Typography>
            )}
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => {
                // Volta ao campo para conferir/alterar o número antes de gerar.
                setPhoneNumber(pairingNumber || phoneNumber);
                setEditingNumber(true);
              }}
            >
              {i18n.t("qrCode.pairing.newCode")}
            </Button>
          </div>
        );
      }

      return (
        <div style={{ textAlign: "center", padding: "12px", minWidth: "256px" }}>
          <TextField
            label={i18n.t("qrCode.pairing.phoneLabel")}
            placeholder="5511999998888"
            variant="outlined"
            margin="dense"
            fullWidth
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
            disabled={loading}
            inputProps={{ inputMode: "numeric" }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={requestPairingCode}
            disabled={loading}
            style={{ marginTop: "12px" }}
          >
            {loading ? i18n.t("qrCode.pairing.generating") : i18n.t("qrCode.pairing.generate")}
          </Button>
          {loading && (
            <div style={{ marginTop: "16px" }}>
              <CircularProgress size={28} />
            </div>
          )}
        </div>
      );
    }

    // Modo QR Code
    if (loading || !qrCode) {
      return (
        <div style={{ textAlign: "center" }}>
          <CircularProgress size={60} />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: "16px" }}>
            {i18n.t("qrCode.generatingQr")}
          </Typography>
        </div>
      );
    }

    // Fundo branco fixo com padding ao redor do QR: a "quiet zone" branca é
    // necessária para a câmera do celular ler o código, e não pode seguir o
    // tema (no modo escuro o Paper do Dialog fica cinza/preto por trás).
    return (
      <div
        style={{
          display: "flex",
          padding: "16px",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
        }}
      >
        <QRCode value={qrCode} size={256} bgColor="#ffffff" fgColor="#000000" />
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" scroll="paper">
      <DialogContent>
        <Paper elevation={0} style={{ display: "flex", alignItems: "center" }}>
          {renderInstructions()}
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minWidth: "256px", minHeight: "256px" }}>
            {renderRightPanel()}
          </div>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
